import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import { DEFAULT_PERIOD } from "../contract";
import usePerformance from "../hooks/usePerformance";
import { datedFilename, downloadBlob } from "../../../lib/download";
import SectionHeading from "../ui/SectionHeading";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import PeriodSelect from "./PeriodSelect";
import MetricGrid from "./MetricGrid";
import SalesChart from "./SalesChart";
import SessionsSplitChart from "./SessionsSplitChart";
import TopOfferingsTable from "./TopOfferingsTable";
import ActivePromotionsTable from "./ActivePromotionsTable";
import { CRM } from "../../../constants/testIds/crm";

/**
 * Zone 3, Performance. The period selector drives everything in the zone; "Export
 * report" downloads the CSV of the visible numbers for the same period (logged server
 * side). All figures aggregate existing bookings, orders and enrolments — nothing here
 * is data entry.
 */
export default function PerformancePanel() {
  const t = useT("crm");
  const tc = useT("common");
  const [period, setPeriod] = useState({ period: DEFAULT_PERIOD });
  const customIncomplete = period.period === "custom" && !(period.from && period.to);
  // An incomplete custom range keeps showing the last complete selection.
  const lastComplete = useRef(period);
  if (!customIncomplete) lastComplete.current = period;
  const applied = customIncomplete ? lastComplete.current : period;
  const { data, loading, loadError, reload } = usePerformance(applied);
  const [exporting, setExporting] = useState(false);

  const exportReport = async () => {
    setExporting(true);
    try {
      const blob = await crmApi.exportPerformance(applied);
      downloadBlob(blob, datedFilename("performance", "csv"));
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc, "errors.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <section aria-labelledby="crm-perf-heading" data-testid={CRM.perf} className="rounded-2xl border border-eh-border-soft bg-white p-5 md:p-6">
      <SectionHeading
        id="crm-perf-heading"
        title={t("perf.title")}
        right={(
          <div className="flex flex-wrap items-center gap-3">
            <PeriodSelect value={period} onChange={setPeriod} />
            <CrmButton variant="link" onClick={exportReport} disabled={exporting || !data} data-testid={CRM.perfExport}>
              {t("perf.export")}
            </CrmButton>
          </div>
        )}
      />
      {loading && !data && <div className="h-40 animate-pulse rounded-2xl bg-eh-cream" aria-busy="true" />}
      {loadError && <CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.perfError} />}
      {data && !loadError && (
        <div className={`space-y-3 ${loading ? "opacity-60" : ""}`}>
          <MetricGrid data={data} />
          <div className="grid gap-3 md:grid-cols-2">
            <SalesChart data={data} />
            <SessionsSplitChart data={data} />
          </div>
          <TopOfferingsTable rows={data.top_offerings} currency={data.currency} />
          <ActivePromotionsTable rows={data.active_promotions} onChanged={reload} />
        </div>
      )}
    </section>
  );
}
